import React, { useState, useEffect } from "react";
import "./nurseroster.css"; // External CSS file
import axios from "axios";
import { APIURL } from "../../Global";
import { useAlert } from "react-alert";
import "simplebar-react/dist/simplebar.min.css";
import { Modal, ModalTitle } from 'react-bootstrap';
import ModalHeader from 'react-bootstrap/esm/ModalHeader';
const AddNurseHoliday = (props) => {
    const [nurses, setNurses] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [daysOff, setDaysOff] = useState(1);
    const [holidays, setHolidays] = useState([])
    const [shiftsPerDay, setShiftsPerDay] = useState(2); // Added state for shifts per day
    const [minNoNurses, setMinNoNurses] = useState(''); 
    const [startDate, setStartDate] = useState(""); // Added state for start date
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const alert = useAlert();

    useEffect(() => {
        const tokenString = sessionStorage.getItem("usertoken");
        const v2 = tokenString?.replace(/["]+/g, ""); // Ensure `tokenString` is handled safely

        const headers = {
            "Content-Type": "application/json",
            Authorization: "Token " + v2,
        };

        const fetchNurses = axios.get(`${APIURL}/api/v1/service-provider/nurse/`, { headers });
        const fetchRosterDetails = axios.get(`${APIURL}/api/v1/service-provider/roster-predetails/`, { headers });
        const fetchCreatedHolidays = axios.get(`${APIURL}/api/v1/service-provider/nurse-holidays/`, { headers });
        Promise.all([fetchNurses, fetchRosterDetails, fetchCreatedHolidays])
            .then(([nursesRes, detailsRes, holidaysRes]) => {
                if (nursesRes.status === 200) {
                    const filteredNurses = nursesRes.data.filter(nurse => nurse.username);
                    setNurses(filteredNurses);
                }
                if (detailsRes.status === 200) {
                    setMinNoNurses(detailsRes.data.min_no_nurses); // Update min_no_nurses
                }
                if (holidaysRes.status === 200) {
                    setHolidays(holidaysRes.data.details);
                }
            })
            .catch((err) => {
                console.error("Error fetching data:", err);
            });
    }, []);

    const isDateBlocked = (date) => {
        const selectedDate = new Date(date);
        return holidays.some((holiday) => {
            const holidayStart = new Date(holiday.from_date);
            const holidayEnd = new Date(holiday.to_date);
            return selectedDate >= holidayStart && selectedDate <= holidayEnd;
        });
    };

    const handleStartDateChange = (date) => {
        if (isDateBlocked(date)) {
            alert.error("The selected start date conflicts with an existing schedule.");
        } else {
            setStartDate(date);
        }
    };

    const calculateEndDate = (startDate) => {
        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + 27); // Add 27 days for a total of 4 weeks (28 days)
        return end.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    };

    const generateSchedule = () => {
        if (!daysOff || daysOff <= 0) {
            alert.error("Please set a valid number of days off per week.");
            return;
        }
    
        if (!minNoNurses || minNoNurses <= 0) {
            alert.error("Please set a valid minimum number of nurses required per shift.");
            return;
        }
    
        const nursesRequiredPerDay = minNoNurses * shiftsPerDay;
        const totalNursesAvailable = nurses.length - daysOff * nurses.length / 7;
    
        // Check if it's possible to satisfy the minimum nurses requirement
        if (totalNursesAvailable < nursesRequiredPerDay) {
            alert.error(
                `Cannot create a schedule because the number of available nurses (${Math.floor(
                    totalNursesAvailable
                )}) is less than the required minimum (${nursesRequiredPerDay}). ` +
                    "Consider decreasing shifts per day, reducing days off, or adding more nurses."
            );
            return;
        }
    
        const totalHolidays = nurses.length * daysOff;
    
        // Calculate base and extra holidays per day
        const baseHolidaysPerDay = Math.floor(totalHolidays / 7);
        const extraHolidays = totalHolidays % 7;
    
        // Create a distribution of holidays across the week
        const holidaysPerDay = Array(7).fill(baseHolidaysPerDay);
        for (let i = 0; i < extraHolidays; i++) {
            holidaysPerDay[i] += 1;
        }
    
        // Initialize the schedule
        const newSchedule = daysOfWeek.reduce((acc, day) => {
            acc[day] = [];
            return acc;
        }, {});
    
        // Initialize days off count for each nurse
        const nurseDaysOff = nurses.map((nurse) => ({
            id: nurse.user,
            name: nurse.name,
            daysOffAssigned: 0,
        }));
    
        // Assign days off for each nurse
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
            let availableSpots = holidaysPerDay[dayIndex];
    
            while (availableSpots > 0) {
                const eligibleNurses = nurseDaysOff.filter(
                    (nurse) => nurse.daysOffAssigned < daysOff && !newSchedule[daysOfWeek[dayIndex]].includes(nurse.id)
                );
    
                if (eligibleNurses.length === 0) {
                    break; // No more eligible nurses for this day
                }
    
                // Shuffle the eligible nurses for fairness
                const shuffledNurses = [...eligibleNurses].sort(() => Math.random() - 0.5);
                const nurse = shuffledNurses[0];
    
                newSchedule[daysOfWeek[dayIndex]].push(nurse.id);
                nurse.daysOffAssigned += 1;
                availableSpots -= 1;
            }
        }
    
        // Check if any nurse didn't get the required days off
        const nursesWithoutFullDaysOff = nurseDaysOff.filter((nurse) => nurse.daysOffAssigned < daysOff);
    
        if (nursesWithoutFullDaysOff.length > 0) {
            const names = nursesWithoutFullDaysOff.map((nurse) => nurse.name).join(", ");
            alert.warning(
                `The following nurses could not be assigned the full ${daysOff} days off per week due to scheduling constraints: ${names}. ` +
                    "You may need to adjust the schedule or reduce the number of days off required."
            );
        }
    
        // Transform the schedule into the required format
        const transformedSchedule = nurses.map((nurse) => {
            const weeklySchedule = daysOfWeek.map((day) =>
                newSchedule[day].includes(nurse.user) ? "Off" : "Work"
            );
            return { id: nurse.user, name: nurse.name, weeklySchedule: [weeklySchedule] };
        });
    
        setSchedule(transformedSchedule);
        alert.success("Schedule generated successfully. Review and finalize.");
    };
        
    const postSchedule = () => {
        const endDate = calculateEndDate(startDate);

        // Helper function to find all dates for a given day of the week within the range
        const findDatesForDay = (dayName) => {
            const dayIndex = daysOfWeek.indexOf(dayName); // Get the index for the day (e.g., Monday = 0, Sunday = 6)
            const dates = [];
            let currentDate = new Date(startDate);

            while (currentDate <= new Date(endDate)) {
                if (currentDate.getDay() === dayIndex) {
                    dates.push(currentDate.toISOString().split("T")[0]); // Format as YYYY-MM-DD
                }
                currentDate.setDate(currentDate.getDate() + 1); // Increment by 1 day
            }

            return dates;
        };

        // Prepare the data to post
        const holidays = schedule.map((nurse) => {
            // Get all off dates for the nurse based on their schedule
            const offDates = daysOfWeek
                .filter((_, index) => nurse.weeklySchedule[0][index] === "Off") // Find "Off" days
                .flatMap(findDatesForDay); // Map them to actual dates

            // Get all working dates for the nurse based on their schedule
            const workingDates = daysOfWeek
                .filter((_, index) => nurse.weeklySchedule[0][index] === "Work") // Find "Work" days
                .flatMap(findDatesForDay); // Map them to actual dates

            return {
                nurse_id: nurse.id,
                off_days: offDates,
                working_days: workingDates,
                from_date: startDate,
                to_date: endDate,
            };
        });
        const dataToPost = { holidays };

        const tokenString = sessionStorage.getItem("usertoken");
        const v2 = tokenString?.replace(/["]+/g, "");

        axios
            .post(`${APIURL}/api/v1/service-provider/nurse-holidays/`, dataToPost, {
                headers: {
                    Authorization: "Token " + v2,
                    "Content-Type": "application/json",
                },
            })
            .then((res) => {
                if (res.data.status === "success") {
                    alert.success("Schedule posted successfully!");
                    props.onSuccess()
                } else {
                    alert.error(res.data.message);
                }
            })
            .catch((err) => {
            });
    };

    const getAdjustedDaysOfWeek = (startDate) => {
        const startDayIndex = new Date(startDate).getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        return [...daysOfWeek.slice(startDayIndex), ...daysOfWeek.slice(0, startDayIndex)];
    };

    return (
        <div className="app">
            <Modal show={true} centered size="lg" onHide={props.onHide}>
                <ModalHeader closeButton>
                    <ModalTitle style={{ fontSize: '18px', margin: 0 }}><b>Add Leave Schedule</b></ModalTitle>
                </ModalHeader>
                <Modal.Body style={{ textAlign: "left", display: 'flex', flexDirection: 'column' }} >
                    <div className="controls">
                        <div className="control-group">
                            <label>Start Date:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => handleStartDateChange(e.target.value)}
                            />
                        </div>
                        <div className="control-group">
                            <label>Days Off Per Week:</label>
                            <input
                                type="number"
                                value={daysOff}
                                onChange={(e) => setDaysOff(Number(e.target.value))}
                            />
                        </div>
                        <div className="control-group">
                            <label>Shifts Per Day:</label>
                            <input
                                type="number"
                                value={shiftsPerDay}
                                onChange={(e) => setShiftsPerDay(e.target.value)}
                            />
                        </div>
                        <div className="control-group">
                            <label>Minimum Working Nurses/Shift:</label>
                            <input type="number" value={minNoNurses} onChange={(e) => setMinNoNurses(e.target.value)} disabled/>
                        </div>
                        <button className="roster-button" onClick={generateSchedule}>
                            Schedule Preview
                        </button>
                        <button className="roster-button" onClick={postSchedule}>
                            Create Schedule
                        </button>
                    </div>
                    {schedule.length > 0 && (
                        <div className="schedule">
                            <h2>Week Schedule</h2>
                            <table className="club-table">
                                <thead>
                                    <tr>
                                        <th>Nurse</th>
                                        {getAdjustedDaysOfWeek(startDate).map((day) => (
                                            <th key={day}>{day}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {schedule.map((nurse) => (
                                        <tr key={nurse.id}>
                                            <td>{nurse.name}</td>
                                            {nurse.weeklySchedule[0].map((status, index) => (
                                                <td key={index} className={status === "Off" ? "off" : "work"}>
                                                    {status}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div >
    );
};

export default AddNurseHoliday;
